import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchJSON, postJSON } from '../utils/api';

export const RecorderAuthContext = createContext(null);

export function RecorderAuthProvider({ children }) {
  const { pathname } = useLocation();
  const shouldBootstrapRecorderAuth = pathname === '/recorder' || pathname.startsWith('/recorder/');
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(shouldBootstrapRecorderAuth);

  const refreshMe = useCallback(async () => {
    const data = await fetchJSON('/api/recorder/auth/me');
    setUser(data.authenticated ? data.recorder_user : null);
    return data;
  }, []);

  useEffect(() => {
    let mounted = true;

    if (!shouldBootstrapRecorderAuth) {
      setBootstrapping(false);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        setBootstrapping(true);
        const data = await fetchJSON('/api/recorder/auth/me');
        if (!mounted) return;
        setUser(data.authenticated ? data.recorder_user : null);
      } catch {
        if (!mounted) return;
        setUser(null);
      } finally {
        if (mounted) setBootstrapping(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [shouldBootstrapRecorderAuth]);

  const signup = useCallback((payload) => {
    return postJSON('/api/recorder/auth/signup', payload);
  }, []);

  const verify = useCallback(async (payload) => {
    const data = await postJSON('/api/recorder/auth/verify', payload);
    setUser(data.recorder_user || null);
    return data;
  }, []);

  const resendCode = useCallback((payload) => {
    return postJSON('/api/recorder/auth/resend-code', payload);
  }, []);

  const login = useCallback(async (payload) => {
    const data = await postJSON('/api/recorder/auth/login', payload);
    setUser(data.recorder_user || null);
    return data;
  }, []);

  const forgotPassword = useCallback((payload) => {
    return postJSON('/api/recorder/auth/forgot-password', payload);
  }, []);

  const resetPassword = useCallback((payload) => {
    return postJSON('/api/recorder/auth/reset-password', payload);
  }, []);

  const logout = useCallback(async () => {
    await postJSON('/api/recorder/auth/logout', {});
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      bootstrapping,
      isAuthenticated: Boolean(user),
      refreshMe,
      signup,
      verify,
      resendCode,
      login,
      forgotPassword,
      resetPassword,
      logout,
    }),
    [user, bootstrapping, refreshMe, signup, verify, resendCode, login, forgotPassword, resetPassword, logout]
  );

  return (
    <RecorderAuthContext.Provider value={value}>
      {children}
    </RecorderAuthContext.Provider>
  );
}
